import React, { createContext, useCallback, useMemo, useState } from 'react'

export type PiPContextType = {
  isSupported: boolean
  pipWindow: Window | null
  requestPipWindow: (width: number, height: number) => Promise<void>
  togglePiP: () => void
  closePipWindow: () => void
}

export const PiPContext = createContext<PiPContextType | undefined>(undefined)

type PiPProviderProps = {
  children: React.ReactNode
}

export function PiPProvider({ children }: PiPProviderProps) {
  const isSupported = 'documentPictureInPicture' in window

  const [pipWindow, setPipWindow] = useState<Window | null>(null)

  const closePipWindow = useCallback(() => {
    if (pipWindow != null) {
      pipWindow.close()
      setPipWindow(null)
    }
  }, [pipWindow])

  const requestPipWindow = useCallback(
    async (width: number, height: number) => {
      if (pipWindow != null) {
        return
      }

      const pip = await (
        window as Window & typeof globalThis & { documentPictureInPicture: any }
      ).documentPictureInPicture.requestWindow({
        width,
        height
      })

      pip.addEventListener('pagehide', () => {
        setPipWindow(null)
      })
      ;[...document.styleSheets].forEach(styleSheet => {
        try {
          const cssRules = [...styleSheet.cssRules].map(rule => rule.cssText).join('')
          const style = document.createElement('style')

          style.textContent = cssRules
          pip.document.head.appendChild(style)
        } catch (e) {
          const link = document.createElement('link')
          if (styleSheet.href == null) {
            return
          }

          link.rel = 'stylesheet'
          link.type = styleSheet.type
          link.media = styleSheet.media.toString()
          link.href = styleSheet.href
          pip.document.head.appendChild(link)
        }
      })

      setPipWindow(pip)
    },
    [pipWindow]
  )

  const startPiP = useCallback(() => {
    requestPipWindow(950, 600).then(() => {
      console.log('PiP window opened')
    })
  }, [requestPipWindow])

  const togglePiP = useCallback(() => {
    return pipWindow ? closePipWindow() : startPiP()
  }, [closePipWindow, pipWindow, startPiP])

  const value = useMemo(() => {
    return {
      isSupported,
      pipWindow,
      requestPipWindow,
      togglePiP,
      closePipWindow
    }
  }, [closePipWindow, isSupported, pipWindow, requestPipWindow, togglePiP])

  return <PiPContext.Provider value={value}>{children}</PiPContext.Provider>
}
